import React from 'react';
import PropTypes from 'prop-types';
import { Async } from 'react-select';
import classnames from 'classnames';
import debouncePromise from 'debounce-promise';

import 'react-select/dist/react-select.css';

import DropdownField from './DropdownField';
import { removeDuplicates } from '../../utility/recentlyUsed';

import './DropdownField.css';

class AsyncDropdownFieldComponent extends DropdownField {
  constructor(props) {
    super(props);
    this.state.defaultValue = props.value;
    this.state.possibleValues = props.value ? [props.value] : [];
    this.getAsyncOptions = debouncePromise(this.getAsyncOptions, 300);
  }

  static get propTypes() {
    return {
      label: PropTypes.string.isRequired,
      value: PropTypes.any,
      mapFn: PropTypes.func,
      recentlyUsed: PropTypes.array,
      editing: PropTypes.bool,
      name: PropTypes.string.isRequired,
      filterField: PropTypes.bool,
      fetchAsyncData: PropTypes.func.isRequired,
      placeholder: PropTypes.string,
      onChange: PropTypes.func,
      className: PropTypes.string,
      updateOnClick: PropTypes.bool.isRequired,
      renderer: PropTypes.func
    };
  }

  static get defaultProps() {
    return {
      mapFn: value => value,
      onChange: () => {},
      validationMessage: 'Invalid Dropdown Field',
      updateOnClick: true
    };
  }

  componentWillReceiveProps(props) {
    if (this.props.value !== props.value) {
      this.setState(
        {
          defaultValue: props.value,
          possibleValues: props.value ? [props.value] : []
        }, () => this.validate());
    }
  }

  onChange(selected) {
    const { mapFn, updateOnClick, recentlyUsed = [] } = this.props;
    const { possibleValues } = this.state;
    const potentialValues = possibleValues.concat(recentlyUsed);

    // TODO: Change to 'reverse map' function?
    const newValue = selected ? potentialValues.find(v => mapFn(v).value === selected.value) : undefined;
    this.setState({ defaultValue: newValue }, () => {
      if (updateOnClick) {
        this.onUpdated();
      }
    });
  }

  showLabel() {
    const { mapFn, editing, filterField } = this.props;
    const { defaultValue } = this.state;
    const value = (mapFn && defaultValue) ? mapFn(defaultValue).value : undefined;
    return !filterField && (!editing || !!value);
  }

  onUpdated() {
    const { onChange } = this.props;
    const { defaultValue } = this.state;
    if (this.validate()) {
      onChange(defaultValue);
    }
  }

  getBaseOptions() {
    const { mapFn, recentlyUsed, filterField } = this.props;
    const { defaultValue } = this.state;
    const currentValue = defaultValue ? mapFn(defaultValue) : undefined;
    if (filterField) {
      return [];
    }
    let options = currentValue ? [currentValue] : [];
    // Need to ensure that at least have the current value in the options
    // or will not display
    if (recentlyUsed) {
      this.addRecentlyUsed(options);
    }
    options = removeDuplicates(options);

    return options;
  }

  getAsyncOptions(inputText) {
    const { mapFn, fetchAsyncData } = this.props;
    return fetchAsyncData(inputText)
      .then(response =>
        response.data
      )
      .then((data) => {
        const possibleValues = data;
        const options = possibleValues.map(mapFn);
        this.setState({ possibleValues });
        // Set complete to true if don't want to hit server again upon
        // more specific queries than already cached.
        return {
          options
        };
      });
  }

  getDropdownOptions(inputText) {
    if (!inputText || inputText.length === 0) {
      const options = {
        options: this.getBaseOptions()
      };
      return Promise.resolve(options);
    }
    return this.getAsyncOptions(inputText);
  }

  reverseMap(option) {
    const { mapFn, recentlyUsed } = this.props;
    const { possibleValues } = this.state;

    const original = option
      ? possibleValues.find(o => mapFn(o).value === option.value)
      : null;

    if (option && recentlyUsed && !original) {
      return recentlyUsed.find(o => mapFn(o).value === option.value);
    }
    return original;
  }

  renderControl() {
    const { mapFn, editing, name, className,
      placeholder, label, required, filterField, renderer } = this.props;
    const { defaultValue } = this.state;
    const placeholderText = placeholder || `${label} ${required ? '' : '(optional)'}`;
    const value = defaultValue ? mapFn(defaultValue).value : undefined;
    const useClass = classnames({ DropdownField: !filterField, 'is-disabled': !editing },
      `select-${name}`, className);

    if (!editing) {
      const displayValue = defaultValue ? mapFn(defaultValue).label : '';
      return (
        <span className={useClass}>
          { renderer
            ? renderer(defaultValue)
            : `${displayValue}\u00a0`
          }
        </span>
      );
    }

    return (
      <Async
        name={name}
        key={value}
        placeholder={placeholderText}
        className={useClass}
        value={filterField ? undefined : value}
        optionClassName="Dropdown-option"
        cache={false}
        filterOptions={false}
        loadOptions={inputText => this.getDropdownOptions(inputText)}
        clearable={!required}
        optionRenderer={option => this.renderOption(option)}
        valueRenderer={renderer ? option => this.renderValue(option) : null}
        onChange={selected => this.onChange(selected)}
        onBlur={() => this.onBlur()}
        disabled={!editing}
        arrowRenderer={editing && !filterField ? undefined : () => {}}
      />
    );
  }
}

export default AsyncDropdownFieldComponent;



// WEBPACK FOOTER //
// ./src/recordDetail/fields/AsyncDropdownField.js